import { landmineNames } from '@mm/constants/landmine-names';
import { apiKeysCRUDActions } from '@mm/state/actions/ApiKeysCRUDActions';
import modalActions from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { GreenMarker } from '../../../../../common/components/GreenMarker/GreenMarker';
import Input from '../../../../../common/components/Input/Input';
import { useGreenCueMarkers } from '../../../../../common/hooks/use-green-cue-markers';
import './ZapierPanel.scss';
import { SyncOutlined } from '@ant-design/icons';
import { ZapierIcon } from '@mm/icons';
import { BaseIntegration } from '../../../beta/modules/Integrations/BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../beta/modules/Integrations/BaseIntegration/BaseIntegrationHeader';

const ZAPIER_EMBED_PLACEHOLDER_ID = 'zapier-panel__zapier-embed-placeholder';
const ZAPIER_EMBED_SCRIPT_ID = 'zapier-panel__zapier-embed-script';
const ZAPIER_EMBED_URL =
  'https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.esm.js';
const ZAPIER_EMBED_STYLESHEET_URL =
  'https://cdn.zapier.com/packages/partner-sdk/v0/zapier-elements/zapier-elements.css';

const ZapierPanel = () => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const apiKeys = useSelector((state: StoreState) => state.apiKeys.entities);
  const busy = useSelector((state: StoreState) => state.apiKeys.busy);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);

  const hasZapierFeature = useSelector(combinedFeatureFlagsSelector).zapier;

  const [isKeyCreationFailing, setIsKeyCreationFailing] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const dispatch: Dispatch<StoreState> = useDispatch();

  const showUpgradeMessage = !busy && !hasZapierFeature;

  const zapierKeys = apiKeys.filter(k => k.connectionType === 'Zapier');
  const apiKey = zapierKeys.find(k => k.active);

  const [apiKeyInputRef, setApiKeyInputRef] = useState<
    HTMLInputElement | undefined
  >(undefined);

  const { isZapierActivated } = useGreenCueMarkers();

  const getApiKeys = async () => {
    if (activeFacebookPage) {
      await dispatch(apiKeysCRUDActions.retrieveAll(activeFacebookPage?.id));
    }
  };

  const createApiKey = async () => {
    if (activeFacebookPage) {
      const response = await dispatch(
        apiKeysCRUDActions.create(activeFacebookPage.id, {
          connectionType: 'Zapier',
          name: 'Zapier API Key',
        }),
      );
      return response;
    }
  };

  const deleteApiKey = async () => {
    if (activeFacebookPage && apiKey) {
      await dispatch(apiKeysCRUDActions.delete(activeFacebookPage.id, apiKey));
    }
  };

  const confirmRegenerateApiKey = (
    onCloseHandler: (okResult: boolean) => void,
  ) => {
    dispatch(
      modalActions.openGeneralConfirmModal({
        headerText: '\u26a0 Regenerate Zapier API Key',
        okButtonText: 'Regenerate Key',
        onCloseHandler,
        messageText: (
          <>
            <p>All Zaps using this API Key will be automatically paused.</p>
            <p>
              The new API Key will need to be added to Zapier to reactivate
              paused Zaps.
            </p>
            <p>This step cannot be undone.</p>
          </>
        ),
      }),
    );
  };

  const openUpgradeModal = (isNoLongerAvailable = false) => {
    const landmineName = isNoLongerAvailable
      ? `${landmineNames.FEATURE_NO_LONGER_AVAILABLE}_${landmineNames.INTEGRATIONS_ZAPIER}`
      : landmineNames.INTEGRATIONS_ZAPIER;

    dispatch(
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName,
        feature: 'zapier',
      }),
    );
  };

  const selectApiKeyText = () => {
    if (showUpgradeMessage) {
      openUpgradeModal();
    }
    setTimeout(() => {
      if (apiKeyInputRef) {
        apiKeyInputRef.setSelectionRange(0, apiKeyInputRef.value.length);
      }
    }, 10);
  };

  const regenerateKey = async () => {
    if (!busy && apiKey && activeFacebookPage) {
      confirmRegenerateApiKey(async ok => {
        if (ok) {
          await deleteApiKey();
          await createApiKey();
        }
      });
    }
  };

  const [copyActionCompleted, setCopyActionCompleted] = useState(false);

  useEffect(() => {
    if (activeFacebookPage) {
      getApiKeys();
    }
  }, [chatbotId]);

  // Create an API key if we don't have one
  useEffect(() => {
    if (
      !busy &&
      apiKeys &&
      !apiKey &&
      !showUpgradeMessage &&
      activeFacebookPage &&
      !isKeyCreationFailing
    ) {
      const createKey = async () => {
        const res = await createApiKey();
        if (res?.error) {
          // Note: We can get more spohisticated at handling the error if needed later.
          // Now it is good enough, being a step forward from never ending loading spinner
          setIsKeyCreationFailing(true);
        }
      };
      createKey();
    }
  }, [busy, apiKeys, activeFacebookPage, isKeyCreationFailing]);

  // Add Zapier Embed to page
  useEffect(() => {
    if (isAccordionOpen) {
      const zapierEmbedScript = document.createElement('script');
      zapierEmbedScript.id = ZAPIER_EMBED_SCRIPT_ID;
      zapierEmbedScript.type = 'module';
      zapierEmbedScript.src = ZAPIER_EMBED_URL;
      document.head.appendChild(zapierEmbedScript);

      const stylesheet = document.createElement('link');
      stylesheet.rel = 'stylesheet';
      stylesheet.href = ZAPIER_EMBED_STYLESHEET_URL;
      document.head.appendChild(stylesheet);

      const element: any = document.createElement('zapier-zap-templates');
      element.theme = 'light';
      element.ids =
        '1559118,1563956,1563958,1563959,1563962,1563963,1563964,1563965,1563967,1563972';
      element.limit = 10;
      element.linkTarget = 'new-tab';
      element.presentation = 'row';
      element.useThisZap = 'show';
      const container = document.getElementById(ZAPIER_EMBED_PLACEHOLDER_ID);
      container?.appendChild(element);

      return () => {
        if (zapierEmbedScript && zapierEmbedScript.parentNode) {
          zapierEmbedScript.parentNode.removeChild(zapierEmbedScript);
        }
      };
    }
  }, [isAccordionOpen]);

  const copyIconClassName = classNames('fa-copy', {
    far: true,
  });

  const sectionProps = {
    landmineName: landmineNames.INTEGRATIONS_ZAPIER,
    featureFlag: 'zapier',
  };

  const header = (
    <PrimaryButton
      className="integrations__header-add-button-secondary"
      onClick={e => {
        e?.stopPropagation();
        window.open(
          'https://zapier.com/apps/mobilemonkey/integrations',
          '_blank',
        );
      }}
    >
      Сreate your own Zap template
    </PrimaryButton>
  );

  return (
    <BaseIntegration
      titleComponent={
        <BaseIntegrationHeader
          title="Zapier"
          upgradeButtonName="Сreate your own Zap template"
          subtitle="Choose from our list of pre-built templates to integrate
          Customers.ai with other applications"
          icon={<ZapierIcon />}
          headerRightComponent={header}
          css={{ icon: 'integrations__zapier-header-icon' }}
          {...sectionProps}
        />
      }
      integrationType="zapier"
      onToggle={() => setIsAccordionOpen(!isAccordionOpen)}
    >
      <div className="zapier-panel__main-container">
        <div className="zapier-panel__details-container">
          <div className="zapier-panel__field-wrapper">
            <div className="zapier-panel__field-container">
              <div className="zapier-panel__field-title">
                <p className="zapier-panel__field-container-name">
                  Copy your API Key
                </p>
                <div className="zapier-panel__api-key-caption">
                  You'll need this key to connect your Zapier account to
                  MobileMonkey for your Facebook Page
                </div>
                {copyActionCompleted && (
                  <span className="zapier-panel__field-title-copied-text">
                    <i className="fas fa-check" /> Copied!
                  </span>
                )}
              </div>
              <div className="zapier-panel__field">
                <div className="zapier-panel__api-key-row">
                  <Input
                    inputRef={setApiKeyInputRef}
                    className="zapier-panel__api-key chatbot__input"
                    readOnly="readonly"
                    value={
                      showUpgradeMessage
                        ? 'Upgrade to unlock this feature!'
                        : (apiKey && apiKey.authToken) || ''
                    }
                    onFocus={selectApiKeyText}
                  />
                  <CopyToClipboard
                    text={(apiKey && apiKey.authToken) || ''}
                    onCopy={() => setCopyActionCompleted(true)}
                  >
                    <PrimaryButton
                      className="integrations__header-title-button"
                      disabled={showUpgradeMessage}
                    >
                      <i className={copyIconClassName} />
                      Copy to clipboard
                    </PrimaryButton>
                  </CopyToClipboard>
                  {!isZapierActivated && (
                    <GreenMarker className="zapier-panel__copy-cue" />
                  )}
                </div>
                <PrimaryButton
                  className="zapier-panel--header-button"
                  onClick={regenerateKey}
                  disabled={showUpgradeMessage}
                >
                  <SyncOutlined color="#2f54eb" />
                  Regenerate Key
                </PrimaryButton>
              </div>
            </div>
          </div>
          {!isZapierActivated && (
            <GreenMarker className="zapier-panel__template-cue" />
          )}
          <div id={ZAPIER_EMBED_PLACEHOLDER_ID} />
        </div>
      </div>
    </BaseIntegration>
  );
};

export default ZapierPanel;
